import { F } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { fmtC } from '../utils/format';
import CoinImage from './CoinImage';
import ExchangeLogo from './ExchangeLogo';
import { OverlayHeader } from './red/OverlayHeader';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTimeDiff(ms, t) {
  const min = Math.round(ms / 60000);
  if (min < 1)  return t('con_time_under_min');
  if (min < 60) return t('con_time_min', { n: min });
  const hrs = Math.round(min / 60);
  if (hrs < 48) return t('con_time_hrs', { n: hrs });
  return t('con_time_days', { n: Math.round(hrs / 24) });
}

const QUALITY_LABEL_KEY = {
  strong: 'con_quality_strong',
  medium: 'con_quality_medium',
  weak:   'con_quality_weak',
};

// ── Komponente ───────────────────────────────────────────────────────────────

export default function LinkTransferSheet({ candidate, marketData, onConfirm, onCancel, isLinking }) {
  const { T } = useTheme();
  const { t } = useT();
  if (!candidate || !candidate.valid) return null;

  const { out, in: inn, amountDiffPct, timeDiffMs, networkFee, quality, blockers, warnings } = candidate;
  const coin     = out.coin || out.sym || '';
  const inCoin   = inn.coin || inn.sym || '';
  const diffAbs  = Math.abs(out.menge - inn.menge);
  const blocked  = blockers.length > 0;
  // Calm-Quality-Töne: sanftes Grün/Gold/Rot aus Theme-Tokens (kein grelles Hex).
  const QUALITY_TONE = {
    strong: T.gain || T.green || '#4F9C73',
    medium: T.gold || '#D6A82E',
    weak:   T.loss || '#C2564E',
  };
  const q        = {
    color: QUALITY_TONE[quality] || QUALITY_TONE.weak,
    label: t(QUALITY_LABEL_KEY[quality] || QUALITY_LABEL_KEY.weak),
  };

  const Row = ({ label, value, accent }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '9px 0', borderBottom: `1px solid ${T.lineSoft || T.line}`,
    }}>
      <span style={{ fontSize: 12.5, color: T.sub, fontFamily: F.family }}>{label}</span>
      <span style={{
        fontSize: 12.5, fontWeight: 700, fontFamily: F.family,
        color: accent || T.text,
      }}>{value}</span>
    </div>
  );

  return (
    <div
      onClick={() => !isLinking && onCancel()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: T.isDark ? 'rgba(0,0,0,0.66)' : 'rgba(60,48,28,0.34)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: T.bg,
          borderRadius: '24px 24px 0 0',
          padding: '14px 22px 40px',
          border: `1px solid ${T.line}`,
          boxShadow: T.isDark ? '0 -22px 60px rgba(0,0,0,0.6)' : '0 -22px 60px rgba(60,48,28,0.26)',
          maxHeight: '88vh', overflowY: 'auto',
          animation: 'slideUp 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        {/* Drag-Handle */}
        <div style={{ width: 40, height: 5, background: T.line, borderRadius: 999, margin: '0 auto 18px' }} />

        <div style={{ marginBottom: 18 }}>
          <OverlayHeader title={t('con_transfer_confirm_title')} sub={t('con_transfer_confirm_sub')} />
        </div>

        {/* ── Flow-Karte ───────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: T.surfaceHi || T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: 16, padding: '14px 14px', marginBottom: 14,
        }}>
          <CoinImage sym={coin} marketData={marketData} size={34} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, color: T.sub, fontWeight: 600, fontFamily: F.family, letterSpacing: '-0.005em' }}>
              {t('con_source')}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: T.text, fontFamily: F.family, maxWidth: '100%' }}>
              <ExchangeLogo name={out.exchange} size={18} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{out.exchange || '—'}</span>
            </div>
            <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family }}>
              {fmtC(out.menge)} {coin} · {out.datum}
            </div>
          </div>
          <svg width={20} height={12} viewBox="0 0 26 10" fill="none"
            stroke={T.sub} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0 }}>
            <path d="M1 5h24M19 1l6 4-6 4"/>
          </svg>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
            <div style={{ fontSize: 11.5, color: T.gain || T.green, fontWeight: 600, fontFamily: F.family, letterSpacing: '-0.005em' }}>
              {t('con_target')}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', fontSize: 13, fontWeight: 700, color: T.text, fontFamily: F.family, maxWidth: '100%' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inn.exchange || '—'}</span>
              <ExchangeLogo name={inn.exchange} size={18} />
            </div>
            <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family }}>
              {fmtC(inn.menge)} {inCoin} · {inn.datum}
            </div>
          </div>
        </div>

        {/* ── Detail-Grid ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 14 }}>
          <Row label={t('con_amount_sent')}     value={`${fmtC(out.menge)} ${coin}`} />
          <Row label={t('con_amount_received')} value={`${fmtC(inn.menge)} ${inCoin}`} />
          <Row
            label={t('con_difference')}
            value={`${fmtC(diffAbs)} ${coin} · ${(amountDiffPct * 100).toFixed(1)} %`}
            accent={amountDiffPct > 0.08 ? (T.gold || '#D6A82E') : T.text}
          />
          {networkFee > 0.0000001 && (
            <Row label={t('con_network_fee')} value={`${fmtC(networkFee)} ${coin}`} />
          )}
          <Row label={t('con_time_difference')} value={formatTimeDiff(timeDiffMs, t)} />
          <Row
            label={t('con_currency')}
            value={candidate.sameCurrency ? `${coin} ✓` : `${coin} → ${inCoin}`}
            accent={candidate.sameCurrency ? (T.gain || T.green) : (T.loss || '#C2564E')}
          />
        </div>

        {/* ── Quality-Indikator ────────────────────────────────────────────── */}
        {!blocked && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: q.color }} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: q.color, fontFamily: F.family }}>
              {q.label}
            </span>
          </div>
        )}

        {/* ── Blocker ──────────────────────────────────────────────────────── */}
        {blockers.map((b, i) => {
          const tone = T.loss || '#C2564E';
          return (
            <div key={`bl-${i}`} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '10px 0', marginBottom: 2,
              borderTop: i === 0 ? `1px solid ${T.lineSoft || T.line}` : 'none',
            }}>
              <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
                stroke={tone} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span style={{ fontSize: 12.5, color: tone, fontWeight: 600, fontFamily: F.family, lineHeight: 1.45 }}>
                {b.message}
              </span>
            </div>
          );
        })}

        {/* ── Warnungen ────────────────────────────────────────────────────── */}
        {!blocked && warnings.map((w, i) => {
          const tone = T.gold || '#D6A82E';
          return (
            <div key={`wn-${i}`} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              padding: '10px 0', marginBottom: 2,
              borderTop: i === 0 ? `1px solid ${T.lineSoft || T.line}` : 'none',
            }}>
              <svg width={17} height={17} viewBox="0 0 24 24" fill="none"
                stroke={tone} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span style={{ fontSize: 12.5, color: tone, fontWeight: 600, fontFamily: F.family, lineHeight: 1.45 }}>
                {w.message}
              </span>
            </div>
          );
        })}

        {/* ── Aktionen ─────────────────────────────────────────────────────── */}
        <button
          disabled={blocked || isLinking}
          onClick={onConfirm}
          style={{
            width: '100%', padding: '15px', marginTop: 6, marginBottom: 10,
            background: (blocked || isLinking) ? (T.surfaceHi || T.surface) : T.text,
            color: (blocked || isLinking) ? T.mute : T.bg,
            border: 'none', borderRadius: 14,
            fontFamily: F.family, fontSize: 15, fontWeight: 800,
            cursor: (blocked || isLinking) ? 'not-allowed' : 'pointer',
          }}
        >
          {isLinking ? t('con_linking') : t('con_link_as_transfer')}
        </button>
        <button
          disabled={isLinking}
          onClick={onCancel}
          style={{
            width: '100%', padding: '13px',
            background: 'none', color: T.sub,
            border: `1px solid ${T.line}`, borderRadius: 14,
            fontFamily: F.family, fontSize: 15, fontWeight: 600,
            cursor: isLinking ? 'not-allowed' : 'pointer',
          }}
        >{t('cancel')}</button>
      </div>
    </div>
  );
}
